import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "it", label: "Italiano" },
];

/**
 * `onDark` is for the dark header: it uses the header nav links' colours. The
 * default suits light surfaces (the mobile menu). The header used to get the
 * light-surface colours, which drew the language name in #1A3535 at 60% on
 * #1A3535 — invisible.
 *
 * `compact` (28 Oct) shows the language code instead of its name below 1280px.
 * The header bar uses it: with the full name, the German header was wider
 * than the screen at 1024px.
 */
export function LanguageSwitcher({ onDark = false, compact = false }: { onDark?: boolean; compact?: boolean }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const current = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
          onDark
            ? "text-[#E8DDCF]/85 hover:text-[#E8DDCF] hover:bg-[#E8DDCF]/10"
            : "text-[#1A3535]/60 hover:text-[#2D7A78] hover:bg-[#F5F2ED]"
        }`}
        aria-label="Change language"
      >
        <Globe size={15} />
        <span className={compact ? "hidden xl:inline" : "hidden sm:inline"}>{current.label}</span>
        {/* 28 Oct: the language code, not a flag emoji (a flag names a country, not a language). */}
        <span className={compact ? "xl:hidden" : "sm:hidden"}>{current.code.toUpperCase()}</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.97 }}
            transition={{ duration: 0.13 }}
            className="absolute right-0 top-full mt-1.5 w-44 bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#DDD8D0] py-2 z-50 overflow-hidden"
          >
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => { i18n.changeLanguage(lang.code); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 mx-0 text-sm transition-colors ${
                  i18n.language === lang.code
                    ? "bg-[#2D7A78]/10 text-[#2D7A78] font-semibold"
                    : "text-[#1A3535]/70 hover:bg-[#F5F2ED]"
                }`}
              >
                <span className="flex-1 text-left">{lang.label}</span>
                {i18n.language === lang.code && <Check size={13} />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
